import { Box } from "@material-ui/core";
import React, { useEffect, useMemo, useState } from "react";
import {
  CallState,
  FlightShopStep,
  IIdLodgings,
  PackagesEntryTypeEnum,
  SELECT_PACKAGE_HOTEL_ROOM,
} from "redmond";
import { RouteComponentProps } from "react-router-dom";
import queryStringParser from "query-string";
import {
  TrackingEventControlType,
  useDeviceTypes,
  useTrackingEventControl,
} from "halifax";
import { PackageHotelShopConnectorProps } from "./container";
import { useExperiments } from "../../context/experiments";
import { DesktopShop } from "./components/DesktopShop";
import { MobileShop } from "./components/MobileShop";
import { PORTAL_TITLE, SELECT_ROOM_TITLE } from "../../lang/textConstants";
import { PATH_FLIGHT_SHOP } from "../../utils/paths";
import { transformToStringifiedQuery } from "./utils/queryStringHelpers";
import { trackEvent } from "../../api/v0/trackEvent";
import { useIsTravelApp } from "@capone/common";

export interface IPackageHotelShopProps
  extends PackageHotelShopConnectorProps,
    RouteComponentProps {}

export const PackageHotelShop = (props: IPackageHotelShopProps) => {
  const {
    fetchPackageHotelShop,
    history,
    // hotelShopCallState,
    selectedLodging,
    fetchTravelWalletDetails,
    listPaymentMethods,
    origin,
    destination,
    packageHotelQueryParams,
    selectedPackageByRateId,
    setPackageEntryPoint,
    fetchTravelWalletCreditHistory,
  } = props;
  const { matchesDesktop, matchesMobile } = useDeviceTypes();
  const [isReadyToRedirect, setIsReadyToRedirect] = useState<boolean>(false);
  const {
    trackingEventControl,
    updateTrackingEventControl,
    removeEventFromTrackingEventControl,
  } = useTrackingEventControl();
  const expState = useExperiments();

  const queryString = useMemo(
    () => queryStringParser.parse(history.location.search),
    [history.location.search]
  );

  const handleReadyToRedirect = (eventControl?: TrackingEventControlType) => {
    // note: it needs to wait for `selectRoomType` to be executed before proceeding with the page redirect
    setTimeout(() => {
      if (eventControl) {
        updateTrackingEventControl(eventControl);
      }
      setIsReadyToRedirect(true);
    });
  };

  const { entryPoint } = queryString;

  useEffect(() => {
    if (entryPoint) {
      setPackageEntryPoint(entryPoint as PackagesEntryTypeEnum);
    }
  }, [entryPoint]);

  const { isTravelApp } = useIsTravelApp();

  useEffect(() => {
    if (isReadyToRedirect) {
      // delayed firing of SELECT_PACKAGE_HOTEL_ROOM
      if (trackingEventControl[SELECT_PACKAGE_HOTEL_ROOM]) {
        const packagePricingDetails =
          selectedPackageByRateId?.packageDetails.pricing;

        const packageDiscount =
          packagePricingDetails?.totalPackageSavings.fiat.value ?? undefined;

        const packageDiscountPercentage =
          packageDiscount && packagePricingDetails
            ? (packageDiscount /
                packagePricingDetails?.strikethroughTotalPrice.fiat.value) *
              100
            : undefined;

        trackEvent({
          eventName: SELECT_PACKAGE_HOTEL_ROOM,
          properties: {
            ...trackingEventControl[SELECT_PACKAGE_HOTEL_ROOM].properties,
            package_discount: packageDiscount,
            package_discount_percentage: packageDiscountPercentage,
          },
        });
        removeEventFromTrackingEventControl(SELECT_PACKAGE_HOTEL_ROOM);
      }

      const {
        selectedLodgingIndex,
        shopToken,
        selectedLodgingIsTopSeller,
        selectedLodgingMealPlan,
        selectedLodgingIsSearched,
        lodgingSelectedFromMap,
      } = queryString;

      const params =
        selectedLodging && origin
          ? transformToStringifiedQuery({
              lodgingId: selectedLodging?.lodging.id,
              origin: origin?.id?.code?.code,
              destination: destination?.label ?? "",
              placeId:
                (destination?.id as IIdLodgings)?.lodgingSelection?.placeId ??
                "",
              selectedLodgingIndex:
                typeof selectedLodgingIndex === "string"
                  ? parseInt(selectedLodgingIndex)
                  : undefined,
              shopToken: shopToken ? (shopToken as string) : undefined,
              ...packageHotelQueryParams,
              isTravelApp,
              selectedLodgingIsTopSeller: selectedLodgingIsTopSeller === "true",
              selectedLodgingMealPlan:
                typeof selectedLodgingMealPlan === "string"
                  ? selectedLodgingMealPlan
                  : undefined,
              selectedLodgingIsSearched: selectedLodgingIsSearched === "true",
              lodgingSelectedFromMap: lodgingSelectedFromMap === "true",
              flightShopProgress: FlightShopStep.ChooseDeparture,
            })
          : "";
      history.push(`${PATH_FLIGHT_SHOP}${params}`, {
        fromPage: location.pathname,
      });
      setIsReadyToRedirect(false);
    }
  }, [history, isReadyToRedirect, trackingEventControl]);

  useEffect(() => {
    if (
      [CallState.Success, CallState.Failed].includes(expState.callState) ||
      expState.experiments.length > 0
    ) {
      fetchPackageHotelShop(history, {
        overrideStateByQueryParams: true,
      });
      setTimeout(() => window.scrollTo(0, 0), 0);
    }
  }, [expState.callState, queryString.lodgingId]);

  useEffect(() => {
    if (selectedLodging) {
      document.title = selectedLodging.lodging.name;
    }
  }, [selectedLodging]);

  useEffect(() => {
    fetchTravelWalletDetails();
    fetchTravelWalletCreditHistory();
    listPaymentMethods();
    document.title = SELECT_ROOM_TITLE;
    setTimeout(() => window.scrollTo(0, 0), 0);

    return () => {
      document.title = PORTAL_TITLE;
    };
  }, []);

  return (
    <Box className="package-hotel-shop-root">
      {matchesDesktop && (
        <DesktopShop handleReadyToRedirect={handleReadyToRedirect} />
      )}
      {matchesMobile && (
        <MobileShop handleReadyToRedirect={handleReadyToRedirect} />
      )}
    </Box>
  );
};
